import apiService from '@/services/ApiService';
import {
  AiGenerationType,
  AiGenerationStatus,
  AiGenerationStatusResponse,
} from '@/services/api-providers/AiGenerationServiceProvider';
import notificationService from '@/services/NotificationService';
import { Logger } from '@/other/Logger';

const log = new Logger('AiGenerationPollingService');

type ProgressCallback = (progress: number, message?: string) => void;
type CompletedCallback = (result: any) => void;
type ErrorCallback = (error: string) => void;

interface PollingOptions {
  onProgress?: ProgressCallback;
  onCompleted?: CompletedCallback;
  onError?: ErrorCallback;
  pollInterval?: number; // milliseconds, default 2000
  maxAttempts?: number; // default 300 (10 minutes @ 2s interval)
}

class AiGenerationPollingService {
  private activePolls: Map<string, NodeJS.Timeout> = new Map();
  private cancelledPolls: Set<string> = new Set();

  /**
   * Start text generation and poll for result
   */
  async generateText(
    data: {
      articleId: string;
      prompt?: string;
      isConsiderArticle?: boolean;
      saveArticle?: boolean;
      selectedText?: string;
      writerType?: string;
      option?: string;
      predefinedPrompt?: string;
    },
    options: PollingOptions = {}
  ): Promise<void> {
    try {
      const response = await apiService.aiGeneration.startTextGeneration(data);
      const { statusId, articleId } = response.data;

      await this.pollStatus(AiGenerationType.TEXT, articleId, statusId, options);
    } catch (error) {
      this.handleStartError(error, options.onError);
    }
  }

  /**
   * Start notes generation and poll for result
   */
  async generateNotes(
    data: {
      articleId: string;
      prompt?: string;
      tone?: string;
      predefinedPrompt?: string;
      saveArticle?: boolean;
      isConsiderArticle?: boolean;
    },
    options: PollingOptions = {}
  ): Promise<void> {
    try {
      // Smart Notes should always consider article context (transcription) by default
      const payload = {
        ...data,
        isConsiderArticle: data.isConsiderArticle !== false, // defaults to true
      };

      const response = await apiService.aiGeneration.startNotesGeneration(payload);
      const { statusId, articleId } = response.data;

      await this.pollStatus(AiGenerationType.NOTES, articleId, statusId, options);
    } catch (error) {
      this.handleStartError(error, options.onError);
    }
  }

  /**
   * Start paragraphs generation and poll for result
   */
  async generateParagraphs(data: { articleId: string }, options: PollingOptions = {}): Promise<void> {
    try {
      const response = await apiService.aiGeneration.startParagraphsGeneration(data);
      const { statusId, articleId } = response.data;

      await this.pollStatus(AiGenerationType.PARAGRAPHS, articleId, statusId, options);
    } catch (error) {
      this.handleStartError(error, options.onError);
    }
  }

  /**
   * Start transcription generation and poll for result
   */
  async generateTranscription(data: { articleId: string }, options: PollingOptions = {}): Promise<void> {
    try {
      const response = await apiService.aiGeneration.startTranscriptionGeneration(data);
      const { statusId, articleId } = response.data;

      await this.pollStatus(AiGenerationType.TRANSCRIPTION, articleId, statusId, options);
    } catch (error) {
      this.handleStartError(error, options.onError);
    }
  }

  /**
   * Start image generation and poll for result
   */
  async generateImage(
    data: {
      articleId?: string;
      prompt: string;
      style?: string;
    },
    options: PollingOptions = {}
  ): Promise<void> {
    try {
      const response = await apiService.aiGeneration.startImageGeneration(data);
      const { statusId, articleId } = response.data;

      await this.pollStatus(AiGenerationType.IMAGE, articleId, statusId, options);
    } catch (error) {
      this.handleStartError(error, options.onError);
    }
  }

  /**
   * Cancel an ongoing generation
   */
  async cancel(type: AiGenerationType, articleId: string): Promise<void> {
    try {
      const pollKey = this.getPollKey(type, articleId);
      this.cancelledPolls.add(pollKey);
      this.stopPolling(pollKey);

      await apiService.aiGeneration.cancelGeneration(type, articleId);
      log.info(`Cancelled generation: ${type} for article ${articleId}`);
    } catch (error) {
      log.error('Failed to cancel generation:', error);
      throw error;
    }
  }

  /**
   * Poll for generation status until completed/error/cancelled
   */
  private async pollStatus(
    type: AiGenerationType,
    articleId: string,
    statusId: string,
    options: PollingOptions
  ): Promise<void> {
    const {
      onProgress,
      onCompleted,
      onError,
      pollInterval = 2000,
      maxAttempts = 300, // 10 minutes @ 2s
    } = options;

    const pollKey = this.getPollKey(type, articleId);

    // Clear any stale cancelled poll keys before starting new generation
    // This prevents race condition where cancel was called before generation started
    this.cancelledPolls.delete(pollKey);

    let attempts = 0;

    // Return a Promise that resolves when polling completes
    return new Promise((resolve, reject) => {
      const poll = async () => {
        // Check if cancelled
        if (this.cancelledPolls.has(pollKey)) {
          this.cancelledPolls.delete(pollKey);
          this.stopPolling(pollKey);
          const errorMsg = 'Generation was cancelled';
          onError?.(errorMsg);
          reject(new Error(errorMsg));
          return;
        }

        // Check max attempts
        if (attempts >= maxAttempts) {
          this.stopPolling(pollKey);
          const errorMsg = 'Generation timed out';
          log.error(errorMsg);
          onError?.(errorMsg);
          notificationService.error(errorMsg);
          reject(new Error(errorMsg));
          return;
        }

        attempts++;

        try {
          const response = await apiService.aiGeneration.getGenerationStatus(type, articleId);
          const status = response.data;

          this.handleStatusUpdate(status, pollKey, onProgress, onCompleted, onError, resolve, reject);
        } catch (error: any) {
          log.error('Failed to poll status:', error);

          // If 404, the status record doesn't exist yet or was cleaned up
          if (error.response?.status === 404) {
            // Continue polling for a bit
            if (attempts < 5) {
              return;
            }
          }

          this.stopPolling(pollKey);
          const errorMsg = error.response?.data?.error || 'Failed to check generation status';
          onError?.(errorMsg);
          notificationService.error(errorMsg);
          reject(new Error(errorMsg));
        }
      };

      // Start polling immediately, then at intervals
      poll();
      const intervalId = setInterval(poll, pollInterval);
      this.activePolls.set(pollKey, intervalId);
    });
  }

  /**
   * Handle status update from polling
   */
  private handleStatusUpdate(
    status: AiGenerationStatusResponse,
    pollKey: string,
    onProgress?: ProgressCallback,
    onCompleted?: CompletedCallback,
    onError?: ErrorCallback,
    resolve?: (value: void | PromiseLike<void>) => void,
    reject?: (reason?: any) => void
  ): void {
    log.info(`Status update: ${status.status} ${status.progress}%`);

    switch (status.status) {
      case AiGenerationStatus.PENDING:
      case AiGenerationStatus.PROCESSING:
        onProgress?.(status.progress);
        break;

      case AiGenerationStatus.COMPLETED: {
        this.stopPolling(pollKey);
        log.info(`Generation completed: ${status.generationType}`);
        onCompleted?.(status.result);
        resolve?.();
        break;
      }

      case AiGenerationStatus.ERROR: {
        this.stopPolling(pollKey);
        const errorMsg = status.error || 'Generation failed';
        log.error(errorMsg);
        onError?.(errorMsg);
        notificationService.error(errorMsg);
        reject?.(new Error(errorMsg));
        break;
      }

      case AiGenerationStatus.CANCELLED: {
        this.stopPolling(pollKey);
        const cancelMsg = 'Generation was cancelled';
        onError?.(cancelMsg);
        reject?.(new Error(cancelMsg));
        break;
      }
    }
  }

  /**
   * Stop polling for a specific generation
   */
  private stopPolling(pollKey: string): void {
    const intervalId = this.activePolls.get(pollKey);
    if (intervalId) {
      clearInterval(intervalId);
      this.activePolls.delete(pollKey);
    }
  }

  /**
   * Get unique poll key for a generation
   */
  private getPollKey(type: AiGenerationType, articleId: string): string {
    return `${type}:${articleId}`;
  }

  /**
   * Handle error when starting generation
   */
  private handleStartError(error: any, onError?: ErrorCallback): void {
    log.error('Failed to start generation:', error);
    const errorMsg = error.response?.data?.error || 'Failed to start generation';
    onError?.(errorMsg);
    notificationService.error(errorMsg);
  }

  /**
   * Clean up all active polls (call on logout/component unmount)
   */
  cleanup(): void {
    this.activePolls.forEach((intervalId) => clearInterval(intervalId));
    this.activePolls.clear();
    this.cancelledPolls.clear();
  }
}

export default new AiGenerationPollingService();
